import {assertUnreachable} from '@wandb/weave/common/util/types';

import {
  type Exact,
  type FetchArtifactCollectionTriggersQuery,
  type FetchProjectTriggersQuery,
  type TriggeredActionType as GQLTriggeredActionType,
  type TriggerFragmentFragment,
  type TriggerScopeFragmentFragment,
} from '../../../../generated/graphql';
import {type QueryResult} from '../../../../state/graphql/apolloWithAbort';
import {splitWithMaxSplits} from '../../../../util/string';
import {isUserVisibleTrigger} from '../../../../util/triggers';
import {getKnownScopeType, hasKnownScopeTypename} from '../TriggerScopeTypes';
import {
  type Trigger,
  type TriggerAction,
  type TriggerActionType,
  type TriggerScope,
} from './utilTypes';

export const getWebhookActionDetails = (trigger: Trigger) => {
  const emptyDetails = {
    webhook: {
      id: '',
      name: '',
      urlEndpoint: '',
      accessTokenRef: '',
      secretRef: '',
      createdAt: new Date().toISOString(),
    },
    actionConfigString: '{}',
    entityName: '',
  };
  const action = trigger.action;
  if (action == null) {
    return emptyDetails;
  }
  if (action.actionType === 'GenericWebhookTriggeredAction') {
    const webhook = action.spec.genericWebhook;
    const actionConfigString = action.spec.requestPayload ?? '{}';
    const entityName = action.spec.entityName ?? '';
    return {webhook, actionConfigString, entityName};
  }
  return emptyDetails;
};

export const getJobActionDetails = (trigger: Trigger) => {
  const emptyDetails = {
    entityName: '',
    projectName: '',
    jobName: {name: '', alias: ''},
    actionConfigString: '{}',
  };
  const action = trigger.action;
  if (action == null) {
    return emptyDetails;
  }
  let fullJobName = '';
  let actionConfigString = '{}';
  let overrides = {};
  if (action.actionType === 'QueueJobTriggeredAction') {
    const template = action.spec.template;
    try {
      const parsedActionTemplate = JSON.parse(template);
      fullJobName = parsedActionTemplate.job;
      overrides = parsedActionTemplate.overrides;
      actionConfigString = JSON.stringify(
        parsedActionTemplate.overrides,
        null,
        2
      );
    } catch (e) {
      // If the template parsing errors out, then leave fullJobName as ''
      console.warn(
        'Error parsing QueueJobTriggeredAction template ==> ',
        template
      );
    }
    const [entityName, projectName, jobNameWithAlias] = splitWithMaxSplits(
      fullJobName ?? '',
      '/',
      2
    );
    const [jobName, alias] = jobNameWithAlias?.split(':') ?? [];
    return {
      entityName,
      projectName,
      jobName: {name: jobName, alias},
      actionConfigString,
      overrides,
    };
  }
  return emptyDetails;
};

type ActionDisplayType =
  | 'Job launch'
  | 'Webhook'
  | 'Slack notification'
  | 'Push notification'
  | 'ARIA'
  | 'No action';

const ACTION_DISPLAY_TYPES: Record<TriggerActionType, ActionDisplayType> = {
  QueueJobTriggeredAction: 'Job launch',
  GenericWebhookTriggeredAction: 'Webhook',
  NotificationTriggeredAction: 'Slack notification',
  PushNotificationTriggeredAction: 'Push notification',
  ARIATriggeredAction: 'ARIA',
  NoOpTriggeredAction: 'No action',
};

// Maps GraphQL `TriggeredActionType` enum name to the GraphQL typename (of the corresponding GraphQL type).
// NOTE: This is intentionally a partial mapping. New action types may be added to the backend schema
// before frontend support is implemented. Unknown action types will return undefined.
const ACTION_TYPENAMES: Partial<
  Record<GQLTriggeredActionType, TriggerActionType>
> = {
  QUEUE_JOB: 'QueueJobTriggeredAction',
  GENERIC_WEBHOOK: 'GenericWebhookTriggeredAction',
  NOTIFICATION: 'NotificationTriggeredAction',
  PUSH_NOTIFICATION: 'PushNotificationTriggeredAction',
  ARIA: 'ARIATriggeredAction',
  NO_OP: 'NoOpTriggeredAction',
};

/**
 * Convert a TriggeredActionType enum name (e.g. 'QUEUE_JOB') to the corresponding GraphQL typename (e.g. 'QueueJobTriggeredAction').
 * Returns undefined for missing or unsupported action types.
 * @param actionType - The GraphQL TriggeredActionType enum name, or undefined.
 * @returns The corresponding GraphQL typename, or undefined if not supported.
 */
export const getActionGQLTypename = (
  actionType?: GQLTriggeredActionType
): TriggerActionType | undefined => {
  return actionType && ACTION_TYPENAMES[actionType];
};

/**
 * Returns the display text for a TriggeredActionType, if known.
 * @param actionType - The GraphQL typename of the triggered action type.
 * @returns The display text for the triggered action type, or undefined if not known.
 */
export const getActionDisplayType = (
  actionType?: TriggerActionType
): ActionDisplayType | undefined => {
  return actionType && ACTION_DISPLAY_TYPES[actionType];
};

export const getActionInfo = (trigger: Trigger) => {
  const {jobName, actionConfigString: jobConfigString} =
    getJobActionDetails(trigger);
  const {webhook, actionConfigString: webhookConfigString} =
    getWebhookActionDetails(trigger);
  const actionType = trigger.action?.actionType;

  const actionTypeName = getActionDisplayType(actionType) ?? '';
  let actionConfigString = '{}';
  let actionName = '';
  let configHeader = null;
  switch (actionType) {
    case 'QueueJobTriggeredAction':
      actionName = jobName.name ?? '';
      actionConfigString = jobConfigString;
      configHeader = 'Overrides';
      break;
    case 'GenericWebhookTriggeredAction':
      actionName = webhook.name;
      actionConfigString = webhookConfigString;
      configHeader = 'Payload';
      break;
    case 'NotificationTriggeredAction':
      actionName = 'Slack notification';
      actionConfigString = 'Slack notification';
      configHeader = null;
      break;
    case 'PushNotificationTriggeredAction':
      actionName = 'Push notification';
      actionConfigString = 'Push notification';
      configHeader = null;
      break;
    case 'ARIATriggeredAction':
      actionName = 'ARIA';
      actionConfigString = trigger.action?.spec.prompt ?? '';
      configHeader = 'Prompt';
      break;
    case 'NoOpTriggeredAction':
      actionName = '—';
      // Other defaults are already correct
      break;
    case undefined:
      console.warn('Invalid trigger action type: undefined');
      break;
    default:
      assertUnreachable(actionType);
  }

  return {
    actionTypeName,
    actionName,
    actionConfigString,
    configHeader,
  };
};

export const toTriggerScope = (
  scope: TriggerScopeFragmentFragment
): TriggerScope => {
  return {
    type: getKnownScopeType(scope),
    id: hasKnownScopeTypename(scope) ? scope.id : '',
  };
};

export const toTrigger = (trigger: TriggerFragmentFragment): Trigger => {
  // Handle triggering condition/event spec
  // TODO: handle other condition types if we add them
  const condition = {
    eventType: trigger.triggeringCondition.eventType,
    filter: trigger.triggeringCondition.filter,
  };

  // Handle triggered action spec
  const triggerAction = trigger.triggeredAction;
  let action: TriggerAction | undefined;

  // Read `readOnly` only after narrowing on `__typename`. New TriggeredAction
  // union members can appear in codegen as `{ __typename }` before this file
  // (or the FE fragment) knows about them; accessing fields on the un-narrowed
  // union would fail typecheck and couples BE schema PRs to FE changes.
  switch (triggerAction?.__typename) {
    case 'QueueJobTriggeredAction': {
      const queue = triggerAction.queue;
      action = {
        actionType: triggerAction.__typename,
        spec: {
          queue: {
            id: queue?.id ?? '',
            name: queue?.name ?? '',
          },
          template: triggerAction.template,
        },
        // Known members declare non-null readOnly; default true if data is missing.
        readOnly: triggerAction.readOnly ?? true,
      };
      break;
    }
    case 'GenericWebhookTriggeredAction': {
      // The schema types `integration` as non-null, but the resolver returns
      // null for a deleted or inaccessible integration, so guard before access.
      const integration = triggerAction.integration;
      if (integration?.__typename === 'GenericWebhookIntegration') {
        action = {
          actionType: triggerAction.__typename,
          spec: {
            genericWebhook: {
              id: integration.id,
              name: integration.name,
              urlEndpoint: integration.urlEndpoint,
              accessTokenRef: integration.accessTokenRef ?? undefined,
              secretRef: integration.secretRef ?? undefined,
              createdAt: integration.createdAt,
            },
            requestPayload: triggerAction.requestPayload,
            entityName: triggerAction.entityName ?? '',
          },
          readOnly: triggerAction.readOnly ?? true,
        };
      }
      break;
    }
    case 'NotificationTriggeredAction': {
      const integration = triggerAction.integration;
      if (integration?.__typename === 'SlackIntegration') {
        action = {
          actionType: triggerAction.__typename,
          spec: {
            integrationID: integration.id,
            entityName: triggerAction.entityName,
            channelName: integration.channelName,
          },
          readOnly: triggerAction.readOnly ?? true,
        };
      }
      break;
    }
    case 'PushNotificationTriggeredAction': {
      action = {
        actionType: triggerAction.__typename,
        spec: {
          title: triggerAction.title ?? '',
          body: triggerAction.body ?? '',
        },
        readOnly: triggerAction.readOnly ?? true,
      };
      break;
    }
    case 'ARIATriggeredAction': {
      action = {
        actionType: triggerAction.__typename,
        spec: {
          prompt: triggerAction.prompt,
        },
        readOnly: triggerAction.readOnly ?? true,
      };
      break;
    }
    case 'NoOpTriggeredAction': {
      // NoOp carries no configuration; the empty spec keeps the discriminated union uniform.
      action = {
        actionType: triggerAction.__typename,
        spec: {},
        readOnly: triggerAction.readOnly ?? true,
      };
      break;
    }
    default:
      // Unknown / not-yet-handled union members (including typename-only codegen
      // stubs) intentionally skip field access.
      console.warn(
        'Invalid trigger action type while parsing triggers for table!'
      );
  }
  return {
    id: trigger.id,
    name: trigger.name,
    description: trigger.description ?? '',
    enabled: trigger.enabled,
    // `createdBy` is a non-null User in the schema, but the resolver returns
    // null once the creating account is removed, so read it defensively.
    createdBy: {
      id: trigger.createdBy?.id ?? '',
      name: trigger.createdBy?.name ?? '',
    },
    createdAt: trigger.createdAt,
    updatedAt: trigger.updatedAt ?? undefined,
    scope: trigger.scope,
    triggeringCondition: condition,
    action,
    lastExecutedAt: trigger.lastExecutedAt ?? undefined,
    readOnly: trigger.readOnly ?? true,
  };
};

export const getTriggersDetails = (
  projectTriggersResult: QueryResult<
    FetchProjectTriggersQuery,
    Exact<{
      entityName: string;
      projectName: string;
    }>
  >,
  acTriggersResult?: QueryResult<
    FetchArtifactCollectionTriggersQuery,
    Exact<{artifactCollectionID: string}>
  >
) => {
  const baseTriggers = projectTriggersResult?.data?.project?.triggers ?? [];
  const projectScopedTriggers = acTriggersResult
    ? baseTriggers.filter(t => t.scope.__typename === 'Project')
    : baseTriggers;
  const acScopedTriggers =
    acTriggersResult?.data?.artifactCollection?.triggers.edges.map(
      e => e?.node
    ) ?? [];
  const triggersData = [...acScopedTriggers, ...projectScopedTriggers];

  // triggersData is a list of triggers as gql types
  const triggerDetails: Trigger[] = triggersData
    .map(toTrigger)
    .filter(isUserVisibleTrigger);
  return triggerDetails;
};
